import { Immutable } from "immer";
import React from "react";
import { t } from "ttag";

import type { SleepSelectionBlock as Value } from "@reactivated";

import { RichTextBlock } from "@thelabnyc/thelabui/src/components/RichText";

type Props = Immutable<{
    value: Value;
}>;

/**
 * React port of the pillows-specific ``SleepSelectionBlock`` (defined in
 * ``server/cms/components/homepage_sleep_selection.py``). DOM mirrors the
 * legacy Django template ``cms/components/homepage_sleep_selection.html``
 * exactly: a desktop tile list (``.pillow-sleeper``) plus a mobile
 * two-row grid (``.pillow-sleeper--mobile``) containing only the first
 * four images split between ``.pillow-sleeper-type__row-1`` (items 0-1)
 * and ``.pillow-sleeper-type__row-2`` (items 2-3).
 */
export const SleepSelectionBlock = (props: Props): React.JSX.Element => {
    const v = props.value;
    const images = v.images ?? [];
    const mobileRow1 = images.slice(0, 2);
    const mobileRow2 = images.slice(2, 4);
    return (
        <>
            <div className="l-full-width pillow-sleeper">
                <div className="pillow-sleeper-inner">
                    <div className="pillow-sleeper__headline">
                        <RichTextBlock value={v.headline ?? ""} />
                    </div>
                    <div className="pillow-sleeper__description">
                        <RichTextBlock value={v.description ?? ""} />
                    </div>
                </div>
                <div className="pillow-sleeper-type__tile-list">
                    {images.map((item, i) => {
                        const imageUrl = item.image?.url ?? "";
                        const linkUrl = item.link?.url ?? "";
                        const sleepType = item.sleep_type ?? "";
                        const shopLabel = t`Shop Pillows for ${sleepType}`;
                        return (
                            <a
                                key={i}
                                href={linkUrl}
                                title={shopLabel}
                                className="pillow-sleeper-type__tile-item"
                                style={{
                                    backgroundImage: `url(${imageUrl})`,
                                }}
                            >
                                <div className="pillow-sleeper-type__tile-cover"></div>
                                <div
                                    className={`pillow-sleeper-type__tile-cover pillow-sleeper-type-${i + 1}`}
                                >
                                    <div className="pillow-sleeper-type__button-wrapper">
                                        <span className="button button-conifer-pillows pillow-sleeper-type__button al-hp__pillow-sleeper-button">
                                            {shopLabel}
                                        </span>
                                    </div>
                                </div>
                            </a>
                        );
                    })}
                </div>
            </div>

            <div className="l-full-width pillow-sleeper--mobile">
                <div className="pillow-sleeper-inner pillow-sleeper-inner--mobile">
                    <div className="pillow-sleeper__headline pillow-sleeper__headline--mobile">
                        <RichTextBlock value={v.headline ?? ""} />
                    </div>
                    <div className="pillow-sleeper__description pillow-sleeper__description--mobile">
                        <RichTextBlock value={v.description ?? ""} />
                    </div>
                </div>
                <div className="pillow-sleeper-type__row-1">
                    {mobileRow1.map((item, i) => {
                        const imageUrl = item.image?.url ?? "";
                        const linkUrl = item.link?.url ?? "";
                        const sleepType = item.sleep_type ?? "";
                        const counter = i + 1;
                        return (
                            <div
                                key={i}
                                className={`pillow-sleeper-type pillow-sleeper-type-${counter} pillow-sleeper-type--mobile`}
                            >
                                <img
                                    className="responsive-img pillow-sleeper-type__image"
                                    src={imageUrl}
                                    alt={sleepType}
                                    loading="lazy"
                                    decoding="async"
                                />
                                <div className="pillow-sleeper-type__button-wrapper pillow-sleeper-type__button-wrapper--mobile">
                                    <a
                                        href={linkUrl}
                                        className="button button-conifer-pillows pillow-sleeper-type__button pillow-sleeper-type__button--mobile"
                                    >
                                        {sleepType}
                                    </a>
                                </div>
                            </div>
                        );
                    })}
                </div>
                <div className="pillow-sleeper-type__row-2">
                    {mobileRow2.map((item, i) => {
                        const imageUrl = item.image?.url ?? "";
                        const linkUrl = item.link?.url ?? "";
                        const sleepType = item.sleep_type ?? "";
                        const counter = i + 3;
                        return (
                            <div
                                key={i}
                                className={`pillow-sleeper-type pillow-sleeper-type-${counter} pillow-sleeper-type--mobile`}
                            >
                                <img
                                    className="responsive-img pillow-sleeper-type__image"
                                    src={imageUrl}
                                    alt={sleepType}
                                    loading="lazy"
                                    decoding="async"
                                />
                                <div className="pillow-sleeper-type__button-wrapper pillow-sleeper-type__button-wrapper--mobile">
                                    <a
                                        href={linkUrl}
                                        className="button button-conifer-pillows pillow-sleeper-type__button pillow-sleeper-type__button--mobile"
                                    >
                                        {sleepType}
                                    </a>
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>
        </>
    );
};
